import axios from "axios";
import { toast } from "react-toastify";

function getAccessToken() {
  return localStorage.getItem("accessToken");
}

export const checkLoginError = (err, router) => {
  if (err.message === "Request timed out") {
    router?.push("/500");
  } else {
    toast.error(
      typeof err?.response?.data?.message === "string"
        ? err?.response?.data?.message
        : "Something went wrong on our side"
    );
  }
};

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL}`
});

axiosInstance.interceptors.request.use(
  (request) => {
    const accessToken = getAccessToken();
    request.headers["x-lang"] = "en";
    request.headers["Content-Type"] =
      request.data instanceof FormData ? "multipart/form-data" : "application/json";

    if (accessToken) {
      request.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    return request;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem("refreshToken");
        const res = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/admin/refreshToken`,
          {
            refreshToken
          },
          {
            headers: {
              "x-lang": "en",
              "Content-Type": "application/json"
            }
          }
        );

        const { accessToken, refreshToken: newRefreshToken } = res?.data?.data;

        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", newRefreshToken);

        originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        localStorage.clear();
        window.location.href = "/session/signin";
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export const getRequest = async (endpoint, router) => {
  try {
    const timeout = 10000;
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out")), timeout)
    );
    const res = await Promise.race([axiosInstance.get(endpoint), timeoutPromise]);

    if (res?.data?.statusCode === 200) {
      return res?.data;
    }
  } catch (error) {
    checkLoginError(error, router);
    console.error(error);
  }
};

export const UploadFiles = async (file) => {
  try {
    let form = new FormData();
    form.append("file", file);
    const res = await axiosInstance.post("/upload/", form);

    if (res?.data?.statusCode === 200) {
      return res?.data?.data?.url;
    }
  } catch (error) {
    checkLoginError(error);
    console.error(error);
    return false;
  }
};

export const UploadMultiple = async (files) => {
  const uploadedFiles = [...files];

  for (let i = 0; i < uploadedFiles.length; i++) {
    const file = uploadedFiles[i];
    if (typeof file === "object" && file !== null) {
      try {
        const url = await UploadFiles(file);
        uploadedFiles[i] = url;
      } catch (error) {
        console.error(`Failed to upload ${file.path}`, error);
      }
    } else {
      console.log(`File ${file} is already uploaded. Skipping...`);
    }
  }
  return uploadedFiles;
};

export const postRequest = async (endpoint, data, router) => {
  try {
    const res = await axiosInstance.post(endpoint, data);

    if (res?.data?.statusCode === 200) {
      return res?.data;
    }
    return res;
  } catch (error) {
    checkLoginError(error, router);
    console.error(error);
  }
};

export const putRequest = async (endpoint, data, router) => {
  try {
    const res = await axiosInstance.put(endpoint, data);

    if (res?.data?.statusCode === 200) {
      return res?.data;
    }
  } catch (error) {
    checkLoginError(error, router);
    console.error(error);
  }
};

export const patchRequest = async (endpoint, data, router) => {
  try {
    const res = await axiosInstance.patch(endpoint, data);

    if (res?.data?.statusCode === 200) {
      return res?.data;
    }
  } catch (error) {
    checkLoginError(error, router);
    console.error(error);
  }
};

export const deleteRequest = async (endpoint, data, router) => {
  try {
    const res = await axiosInstance.delete(endpoint, { data });

    if (res?.data?.statusCode === 200) {
      return res?.data;
    }
    return false;
  } catch (error) {
    checkLoginError(error, router);
    console.error(error);
  }
};
